<template>
  <div class="am3-input-container text-sm">
    <!-- Header with Label and Toolbar -->
    <div class="flex items-center justify-between -mb-1">
      <AM3InputLabel
        :label="label"
        :required="required"
        :input-id="inputId"
        class="mb-1.5"
      />

      <!-- WYSIWYG Toolbar -->
      <div class="am3-wysiwyg-toolbar flex items-center gap-1 transition-shadow  shadow-sm focus-within:shadow-lg hover:shadow-lg -top-2 relative border border-surface-dark bg-surface-light rounded-md">
        <!-- Font Size Dropdown -->
        <div
          v-if="enabledControls.fontSize"
          class="relative"
        >
          <button
            type="button"
            class="flex items-center gap-1 px-2 py-1 text-sm rounded hover:bg-surface-dark"
            @click="showFontSizeDropdown = !showFontSizeDropdown"
          >
            {{ currentFontSize }}
            <AM3Icon
              type="chevron-down"
              size="sm"
            />
          </button>
          <div
            v-if="showFontSizeDropdown"
            class="absolute top-full left-0 z-10 bg-white border border-surface-dark rounded-md hover:border-brand shadow-lg"
          >
            <button
              v-for="size in fontSizes"
              :key="size.value"
              type="button"
              class="block w-full px-3 py-2 text-left text-sm hover:bg-surface-light"
              @click="setFontSize(size.value)"
            >
              {{ size.label }}
            </button>
          </div>
        </div>

        <!-- Divider -->
        <div
          v-if="showFormatDivider"
          class="w-px mx-2 h-6 bg-surface-dark"
        />

        <!-- Format Buttons -->
        <button
          v-if="enabledControls.bold"
          v-tooltip.top="`Bold`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center "
          :class="{ 'bg-brand hover:bg-brand-light hover:text-primary text-inverse': isBold }"
          title="Bold"
          @click="toggleFormat('bold')"
        >
          <AM3Icon
            type="bold"
            size="sm"
          />
        </button>

        <button
          v-if="enabledControls.italic"
          v-tooltip.top="`Italic`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
          :class="{ 'bg-brand hover:bg-brand-light hover:text-primary text-inverse': isItalic }"
          title="Italic"
          @click="toggleFormat('italic')"
        >
          <AM3Icon
            type="italic"
            size="sm"
          />
        </button>

        <button
          v-if="enabledControls.underline"
          v-tooltip.top="`Underline`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
          :class="{ 'bg-brand hover:bg-brand-light hover:text-primary text-inverse': isUnderline }"
          title="Underline"
          @click="toggleFormat('underline')"
        >
          <AM3Icon
            type="underline"
            size="sm"
          />
        </button>

        <!-- Divider -->
        <div
          v-if="showAlignDivider"
          class="w-px h-6 bg-surface-dark"
        />

        <!-- Alignment Buttons -->
        <button
          v-if="enabledControls.align"
          v-tooltip.top="`Align left`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
          :class="{ 'bg-brand hover:bg-brand-light hover:text-primary text-inverse': currentAlign === 'left' }"
          title="Align left"
          @click="setAlign('left')"
        >
          <AM3Icon
            type="bars-3-bottom-left"
            size="sm"
          />
        </button>

        <button
          v-if="enabledControls.align"
          v-tooltip.top="`Align centre`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
          :class="{ 'bg-brand hover:bg-brand-light hover:text-primary text-inverse': currentAlign === 'center' }"
          title="Align centre"
          @click="setAlign('center')"
        >
          <AM3Icon
            type="bars-3-center-left"
            size="sm"
          />
        </button>

        <button
          v-if="enabledControls.align"
          v-tooltip.top="`Align right`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
          :class="{ 'bg-brand hover:bg-brand-light hover:text-primary text-inverse': currentAlign === 'right' }"
          title="Align right"
          @click="setAlign('right')"
        >
          <AM3Icon
            type="bars-3-bottom-right"
            size="sm"
          />
        </button>

        <button
          v-if="enabledControls.align"
          v-tooltip.top="`Justify`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
          :class="{ 'bg-brand hover:bg-brand-light hover:text-primary text-inverse': currentAlign === 'justify' }"
          title="Justify"
          @click="setAlign('justify')"
        >
          <AM3Icon
            type="bars-3"
            size="sm"
          />
        </button>

        <!-- Divider -->
        <div
          v-if="showListDivider"
          class="w-px h-6 bg-surface-dark"
        />

        <!-- List Buttons -->
        <button
          v-if="enabledControls.lists"
          v-tooltip.top="`Bulleted list`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
          :class="{ 'bg-brand hover:bg-brand-light hover:text-primary text-inverse': isBulletList }"
          title="Bulleted list"
          @click="toggleList('unordered')"
        >
          <AM3Icon
            type="list-bullet"
            size="sm"
          />
        </button>

        <button
          v-if="enabledControls.lists"
          v-tooltip.top="`Numbered list`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
          :class="{ 'bg-brand hover:bg-brand-light hover:text-primary text-inverse': isNumberedList }"
          title="Numbered list"
          @click="toggleList('ordered')"
        >
          <AM3Icon
            type="numbered-list"
            size="sm"
          />
        </button>

        <!-- Divider -->
        <div
          v-if="showLinkDivider"
          class="w-px h-6 bg-surface-dark"
        />

        <!-- Link Button -->
        <div
          v-if="enabledControls.link"
          class="relative"
        >
          <button
            v-tooltip.top="`Add Link`"
            type="button"
            class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
            title="Add Link"
            hover:bg-brand-light
            hover:text-primary
            @click="toggleLink"
          >
            <AM3Icon
              type="link"
              size="sm"
            />
          </button>

          <!-- Link Dropdown -->
          <div
            v-if="showLinkModal"
            class="link-dropdown absolute top-full right-0 z-10 bg-white border border-surface-dark rounded-md shadow-sm focus-within:shadow-lg hover:shadow-lg p-4 w-80"
          >
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-1">URL</label>
                <AM3InputText
                  v-model="linkUrl"
                  type="text"
                  placeholder="https://example.com"
                  :validate="validateLinkUrl"
                  @keydown.enter="insertLink"
                  @keydown.escape="cancelLink"
                />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">Link Text</label>
                <AM3InputText
                  v-model="linkText"
                  type="text"
                  placeholder="Link text"

                  @keydown.enter="insertLink"
                  @keydown.escape="cancelLink"
                />
              </div>
              <div class="flex justify-end gap-2">
                <AM3Button
                  class="px-3 py-1 text-sm text-secondary hover:text-gray-800"
                  type="secondary"
                  size="sm"
                  text="Cancel"
                  @click="cancelLink"
                />

                <AM3Button
                  type="primary"
                  size="sm"
                  class="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="!linkUrl.trim() || !linkText.trim()"
                  text="Add Link"
                  @click="insertLink"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Remove Link Button -->
        <button
          v-if="enabledControls.link && isLink"
          v-tooltip.top="`Remove Link`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center text-red-600"
          title="Remove Link"
          hover:bg-brand-light
          hover:text-primary
          @click="removeLink"
        >
          <AM3Icon
            type="link-slash"
            size="sm"
          />
        </button>

        <!-- Clear Formatting Button -->
        <button
          v-if="enabledControls.clearFormatting"
          v-tooltip.top="`Clear Formatting`"
          type="button"
          class="p-1 rounded hover:bg-brand-light outline-transparent focus:outline-brand size-8 flex flex-col items-center justify-center"
          title="Clear Formatting"
          @click="clearFormatting"
        >
          <AM3Icon
            type="trash"
            size="sm"
          />
        </button>
      </div>
    </div>

    <div
      class="am3-input-wrapper flex flex-row items-start justify-between"
      :class="[
        disabled ? 'disabled' : 'focus-within:outline-brand',
        (disabled) ? disabledClasses : 'bg-surface-light hover:border-brand',
        (validationState === false || typeof validationState === 'string') ? 'error border border-solid border-[var(--font-colour-warning)]' : buttonBorderClasses,
      ]"
      :data-test-id="generateDataTestId(dataTestId, 'input-wrapper')"
    >
      <!-- Rich Text Editor -->
      <div
        :id="inputId"
        ref="editorRef"
        class="am3-input-wysiwyg focus:outline-brand focus:outline-offset-0 am3-input bg-transparent px-3 py-2.5 text-sm min-h-20 w-full border-none outline-none focus:ring-0 rounded-md relative"
        :class="{ 'text-disabled cursor-not-allowed': disabled }"
        :contenteditable="!disabled"
        :data-placeholder="placeholder"
        :data-test-id="generateDataTestId(dataTestId, 'wysiwyg')"
        @input="handleInput"
        @keydown="handleKeyDown"
        @paste="handlePaste"
        @blur="handleBlur"
        @mouseup="updateToolbarState"
        @keyup="updateToolbarState"
      />
    </div>

    <AM3InputError
      :error="validationState"
      :data-test-id="generateDataTestId(dataTestId, 'input-error')"
    />
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, ref, nextTick, onMounted, onUnmounted, watch, computed } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import '@/components/am3-generic/core/theme.css'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'
import { useFormValidation } from '@/components/am3-generic/inputs/useFormValidation'
import AM3InputError from '@/components/am3-generic/inputs/AM3InputError.vue'
import AM3InputLabel from '@/components/am3-generic/inputs/AM3InputLabel.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { isValidUrl } from '@/utils/url'

/**
 * AM3InputTextareaWysiwyg component
 *
 * A WYSIWYG rich text editor that wraps AM3InputTextarea with formatting controls
 * Returns HTML content via v-model
 */
export default defineComponent({
  name: 'AM3InputTextareaWysiwyg',
  components: {
    AM3InputError,
    AM3InputLabel,
    AM3Icon,
  },

  props: {
    // v-model value (HTML content)
    modelValue: {
      type: String,
      default: '',
    },

    // Input label (string or false to hide)
    label: {
      type: [String, Boolean] as PropType<string | false>,
      default: '',
    },

    // Placeholder text
    placeholder: {
      type: String,
      default: 'Enter text...',
    },

    // Number of visible text lines
    rows: {
      type: Number,
      default: 4,
    },

    // Disabled state
    disabled: {
      type: Boolean,
      default: false,
    },

    // Required field
    required: {
      type: Boolean,
      default: false,
    },

    // Validation function
    validate: {
      type: Function as PropType<(value: string) => boolean | string>,
      default: () => true,
    },

    // Validate on blur
    validateOnBlur: {
      type: Boolean,
      default: false,
    },

    // Data test id prefix
    dataTestId: {
      type: String,
      default: 'wysiwyg-input',
    },

    // Controls to show/hide
    controls: {
      type: Array as PropType<string[]>,
      default: () => ['bold', 'italic', 'underline', 'link'],
    },
  },

  emits: ['update:modelValue', 'change', 'blur'],

  setup(props, { emit }) {
    const buttonBorderClasses = ' rounded-md border border-1 border-surface-dark outline-1 outline-offset-0 outline-transparent outline-none'
    const disabledClasses = ' text-disabled cursor-not-allowed hover:border-surface-dark hover:outline-none text-white'

    // Generate unique ID for the editor
    const inputId = ref(`am3-wysiwyg-${uuidv4()}`)
    const editorRef = ref<HTMLDivElement | null>(null)

    // Toolbar state
    const isBold = ref(false)
    const isItalic = ref(false)
    const isUnderline = ref(false)
    const isLink = ref(false)
    const isBulletList = ref(false)
    const isNumberedList = ref(false)
    const currentAlign = ref<'left' | 'center' | 'right' | 'justify'>('left')
    const showFontSizeDropdown = ref(false)
    const currentFontSize = ref('Medium')

    // Link modal state
    const showLinkModal = ref(false)
    const linkUrl = ref('')
    const linkText = ref('')
    const selectedText = ref('')
    const currentRange = ref<Range | null>(null)

    // Font sizes
    const fontSizes = [
      { label: 'Small', value: '1' },
      { label: 'Medium', value: '3' },
      { label: 'Large', value: '5' },
      { label: 'Extra Large', value: '7' },
    ]

    // Custom validation function that handles HTML content
    const customValidate = (value: string) => {
      // For WYSIWYG, we need to check if the content is actually empty
      // Strip HTML tags and check if there's meaningful content
      const textContent = value.replace(/<[^>]*>/g, '').trim()

      // First check if it's required and empty
      if (props.required && !textContent) {
        return 'This field is required'
      }

      // Then run the custom validation if provided
      if (props.validate) {
        return props.validate(value)
      }

      return true
    }

    // Setup form validation with custom validation function
    const {
      validationState,
      runValidation: _runValidation,
      resetValidation,
      validateOnBlur,
      handleBlur: _handleBlur,
    } = useFormValidation(customValidate, props.modelValue, false) // Set required to false since we handle it in customValidate

    // Sync the validateOnBlur prop with the composable ref (same as AM3InputText)
    validateOnBlur.value = props.validateOnBlur

    // Controls configuration
    const enabledControls = computed(() => {
      const controls = props.controls || []
      return {
        fontSize: controls.includes('fontSize'),
        bold: controls.includes('bold'),
        italic: controls.includes('italic'),
        underline: controls.includes('underline'),
        align: controls.includes('align'),
        lists: controls.includes('lists'),
        link: controls.includes('link'),
        clearFormatting: controls.includes('clearFormatting'),
      }
    })

    // Show dividers only if there are controls on both sides
    const showFormatDivider = computed(() => {
      const hasFormatControls = enabledControls.value.bold || enabledControls.value.italic || enabledControls.value.underline
      return enabledControls.value.fontSize && hasFormatControls
    })

    const showAlignDivider = computed(() => {
      const hasPrecedingControls = enabledControls.value.fontSize || enabledControls.value.bold || enabledControls.value.italic || enabledControls.value.underline
      return enabledControls.value.align && hasPrecedingControls
    })

    const showListDivider = computed(() => {
      const hasPrecedingControls = enabledControls.value.fontSize || enabledControls.value.bold || enabledControls.value.italic || enabledControls.value.underline || enabledControls.value.align
      return enabledControls.value.lists && hasPrecedingControls
    })

    const showLinkDivider = computed(() => {
      const hasPrecedingControls = enabledControls.value.fontSize || enabledControls.value.bold || enabledControls.value.italic || enabledControls.value.underline || enabledControls.value.align || enabledControls.value.lists
      const hasLinkControls = enabledControls.value.link || enabledControls.value.clearFormatting
      return hasPrecedingControls && hasLinkControls
    })

    // Handle blur with custom logic
    const handleBlur = () => {
      _handleBlur(props.modelValue)
      emit('blur')
    }

    // Public method to run validation
    const runValidation = (value = props.modelValue, checkRequired = props.required) => {
      return _runValidation(value, checkRequired)
    }

    // Update model value
    const handleInput = () => {
      if (editorRef.value && !isInternalUpdate.value) {
        let html = editorRef.value.innerHTML

        // Normalize empty content - if only contains <br>, whitespace, or empty tags, return empty string
        const textContent = html.replace(/<[^>]*>/g, '').trim()
        if (!textContent || html.trim() === '<br>' || html.trim() === '<div><br></div>') {
          html = ''
        }

        isInternalUpdate.value = true
        emit('update:modelValue', html)
        emit('change', html)
        nextTick(() => {
          isInternalUpdate.value = false
        })
      }
    }

    // Set initial content
    const setContent = (html: string) => {
      if (editorRef.value && editorRef.value.innerHTML !== html && !document.activeElement?.closest(`#${inputId.value}`)) {
        // Save cursor position if editor is focused
        const selection = window.getSelection()
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null
        const isEditorFocused = document.activeElement === editorRef.value

        editorRef.value.innerHTML = html

        // Restore cursor position if editor was focused
        if (isEditorFocused && range && editorRef.value.contains(range.startContainer)) {
          try {
            selection?.removeAllRanges()
            selection?.addRange(range)
          }
          catch (e) {
            // Ignore errors when restoring selection
          }
        }
      }
    }

    // Update toolbar state based on current selection
    const updateToolbarState = () => {
      if (!document.getSelection) return

      const selection = document.getSelection()
      if (!selection || selection.rangeCount === 0) return

      isBold.value = document.queryCommandState('bold')
      isItalic.value = document.queryCommandState('italic')
      isUnderline.value = document.queryCommandState('underline')

      // List state
      isBulletList.value = document.queryCommandState('insertUnorderedList')
      isNumberedList.value = document.queryCommandState('insertOrderedList')

      // Alignment state
      if (document.queryCommandState('justifyCenter')) {
        currentAlign.value = 'center'
      }
      else if (document.queryCommandState('justifyRight')) {
        currentAlign.value = 'right'
      }
      else if (document.queryCommandState('justifyFull')) {
        currentAlign.value = 'justify'
      }
      else {
        currentAlign.value = 'left'
      }

      // Check if selection is within a link
      const range = selection.getRangeAt(0)
      const container = range.commonAncestorContainer
      const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container as Element
      isLink.value = !!element?.closest('a')

      // Update font size
      const fontSize = document.queryCommandValue('fontSize')
      const sizeObj = fontSizes.find(s => s.value === fontSize)
      currentFontSize.value = sizeObj?.label || 'Medium'
    }

    // Execute formatting command
    const execCommand = (command: string, value?: string) => {
      document.execCommand(command, false, value)
      handleInput()
      updateToolbarState()
    }

    // Toggle format
    const toggleFormat = (format: 'bold' | 'italic' | 'underline') => {
      execCommand(format)
    }

    // Set font size
    const setFontSize = (size: string) => {
      execCommand('fontSize', size)
      showFontSizeDropdown.value = false
    }

    // Set text alignment
    const setAlign = (align: 'left' | 'center' | 'right' | 'justify') => {
      const commandMap = {
        left: 'justifyLeft',
        center: 'justifyCenter',
        right: 'justifyRight',
        justify: 'justifyFull',
      } as const
      execCommand(commandMap[align])
    }

    // Toggle list
    const toggleList = (type: 'unordered' | 'ordered') => {
      execCommand(type === 'unordered' ? 'insertUnorderedList' : 'insertOrderedList')
    }

    // Toggle link
    const toggleLink = () => {
      const selection = document.getSelection()

      // Store the current range to restore it later
      if (selection && selection.rangeCount > 0) {
        currentRange.value = selection.getRangeAt(0).cloneRange()
        selectedText.value = selection.toString()
      }
      else {
        // If no selection, create a range at current cursor position
        if (editorRef.value && selection) {
          const range = document.createRange()
          range.selectNodeContents(editorRef.value)
          range.collapse(false) // Collapse to end
          currentRange.value = range
          selectedText.value = ''
        }
      }

      linkText.value = selectedText.value.trim() || 'Link text'
      linkUrl.value = ''
      showLinkModal.value = true

      // Focus the URL input after the dropdown appears
      nextTick(() => {
        const urlInput = document.querySelector('.link-dropdown input[type="text"]') as HTMLInputElement
        if (urlInput) {
          urlInput.focus()
        }
      })
    }

    // Insert link
    const insertLink = () => {
      if (!linkUrl.value.trim() || !linkText.value.trim()) return

      // Ensure URL has a protocol
      let finalUrl = linkUrl.value.trim()
      if (!finalUrl.match(/^https?:\/\//)) {
        finalUrl = 'https://' + finalUrl
      }

      // Focus the editor and restore the selection
      if (editorRef.value) {
        editorRef.value.focus()

        if (currentRange.value) {
          const selection = document.getSelection()
          if (selection) {
            selection.removeAllRanges()
            selection.addRange(currentRange.value)
          }
        }
      }

      // Create the link HTML
      const linkHtml = `<a href="${finalUrl}" target="_blank" rel="noopener noreferrer" class="wysiwyg-link">${linkText.value}</a>`

      // Small delay to ensure focus and selection are restored
      setTimeout(() => {
        // If there's selected text, replace it; otherwise insert at cursor
        if (selectedText.value.trim()) {
          execCommand('insertHTML', linkHtml)
        }
        else {
          // Insert link at current cursor position
          execCommand('insertHTML', linkHtml)
        }

        // Clean up
        showLinkModal.value = false
        linkUrl.value = ''
        linkText.value = ''
        selectedText.value = ''
        currentRange.value = null
      }, 10)
    }

    // URL validation function for link URL
    const validateLinkUrl = (value: string) => {
      if (!value || value.trim() === '') return true // Allow empty
      return isValidUrl(value) || 'Please enter a valid URL'
    }

    // Cancel link modal
    const cancelLink = () => {
      showLinkModal.value = false
      linkUrl.value = ''
      linkText.value = ''
      selectedText.value = ''
      currentRange.value = null
    }

    // Remove link
    const removeLink = () => {
      const selection = document.getSelection()
      if (!selection || selection.rangeCount === 0) return

      // Find the link element that contains the current cursor position
      const range = selection.getRangeAt(0)
      const container = range.commonAncestorContainer
      const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container as Element
      const linkElement = element?.closest('a') as HTMLAnchorElement

      if (linkElement && editorRef.value?.contains(linkElement)) {
        // Focus the editor
        editorRef.value.focus()

        // Select the ENTIRE link element (including the tags)
        const newRange = document.createRange()
        newRange.selectNode(linkElement) // This selects the whole <a> element
        selection.removeAllRanges()
        selection.addRange(newRange)

        // Now that the entire link is selected, unlink should work
        if (document.queryCommandSupported && document.queryCommandSupported('unlink')) {
          document.execCommand('unlink')
          handleInput()
          updateToolbarState()
        }
        else {
          // Fallback: direct DOM manipulation
          const parent = linkElement.parentNode
          if (parent) {
            const textContent = linkElement.textContent || ''
            const textNode = document.createTextNode(textContent)
            parent.replaceChild(textNode, linkElement)

            // Position cursor after the text
            const afterRange = document.createRange()
            afterRange.setStartAfter(textNode)
            afterRange.setEndAfter(textNode)
            selection.removeAllRanges()
            selection.addRange(afterRange)

            handleInput()
            updateToolbarState()
          }
        }
      }
    }

    // Clear all formatting
    const clearFormatting = () => {
      execCommand('removeFormat')
    }

    // Handle keydown event
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        handleInput()
      }
    }

    // Handle paste event
    const handlePaste = (event: ClipboardEvent) => {
      event.preventDefault()
      const text = event.clipboardData?.getData('text/plain') || ''
      execCommand('insertText', text)
    }

    // Close dropdowns when clicking outside
    const handleClickOutside = (event: Event) => {
      const target = event.target as Element
      if (!target.closest('.am3-wysiwyg-toolbar')) {
        showFontSizeDropdown.value = false
        showLinkModal.value = false
      }
    }

    // Track if we're updating from internal changes to prevent infinite loops
    const isInternalUpdate = ref(false)

    onMounted(() => {
      // Set initial content
      nextTick(() => {
        if (props.modelValue && editorRef.value) {
          isInternalUpdate.value = true
          setContent(props.modelValue)
          nextTick(() => {
            isInternalUpdate.value = false
          })
        }
      })

      // Add click outside listener
      document.addEventListener('click', handleClickOutside)
    })

    // Watch for prop changes and update editor content
    watch(
      () => props.modelValue,
      (newValue) => {
        if (!isInternalUpdate.value && editorRef.value) {
          isInternalUpdate.value = true
          setContent(newValue || '')
          nextTick(() => {
            isInternalUpdate.value = false
          })
        }
      },
    )

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    return {
      inputId,
      editorRef,
      validationState,
      handleInput,
      handleKeyDown,
      handlePaste,
      buttonBorderClasses,
      disabledClasses,
      generateDataTestId,
      handleBlur,
      runValidation,
      resetValidation,
      isInternalUpdate,

      // Controls
      enabledControls,
      showFormatDivider,
      showAlignDivider,
      showListDivider,
      showLinkDivider,

      // Toolbar state
      isBold,
      isItalic,
      isUnderline,
      isLink,
      isBulletList,
      isNumberedList,
      currentAlign,
      showFontSizeDropdown,
      currentFontSize,
      fontSizes,

      // Toolbar actions
      toggleFormat,
      setFontSize,
      setAlign,
      toggleList,
      toggleLink,
      removeLink,
      clearFormatting,
      updateToolbarState,

      // Link modal
      showLinkModal,
      linkUrl,
      linkText,
      insertLink,
      cancelLink,
      validateLinkUrl,
      currentRange,
    }
  },
})
</script>

<style>
.am3-wysiwyg-toolbar {
  font-family: inherit;
}

.am3-input-wysiwyg {
  /* min-height: calc(v-bind(rows) * 1.5rem + 1rem); */
  resize: vertical;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.am3-input-wysiwyg:empty:before {
  content: attr(data-placeholder);
  color: var(--secondary-text-color);
  position: absolute;
  cursor: text;
}

/* List rendering (Tailwind preflight strips these by default) */
.am3-input-wysiwyg ul {
  list-style: disc;
  padding-left: 1.5rem;
}

.am3-input-wysiwyg ol {
  list-style: decimal;
  padding-left: 1.5rem;
}

.am3-input-wysiwyg li {
  display: list-item;
}

/* Rich text formatting styles */
.am3-input-wysiwyg a,
.am3-input-wysiwyg a:link,
.am3-input-wysiwyg a:visited,
.am3-input-wysiwyg .wysiwyg-link {
  color: #3b82f6 !important;
  text-decoration: underline !important;
  cursor: pointer;
  background: none !important;
  border: none !important;
  outline: none !important;
}

.am3-input-wysiwyg a:hover,
.am3-input-wysiwyg .wysiwyg-link:hover {
  color: #2563eb !important;
  text-decoration: underline !important;
}

/* Ensure links are visible even when editor is focused */
.am3-input-wysiwyg:focus a,
.am3-input-wysiwyg:focus a:link,
.am3-input-wysiwyg:focus a:visited,
.am3-input-wysiwyg:focus .wysiwyg-link {
  color: #3b82f6 !important;
  text-decoration: underline !important;
}

/* Override any contenteditable default styles */
.am3-input-wysiwyg[contenteditable] a,
.am3-input-wysiwyg[contenteditable] .wysiwyg-link {
  color: #3b82f6 !important;
  text-decoration: underline !important;
}
</style>
